import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  LayoutDashboard,
  MessageSquare,
  Archive,
  BarChart3,
  Users2,
  Code,
  Settings,
  Search,
  Phone,
  Mail,
  Calendar,
  Bot,
  Plug,
  Webhook,
  Shield,
  Users,
} from 'lucide-react'

interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const pages = [
  { name: 'Dashboard', path: '/', icon: LayoutDashboard, keywords: ['home', 'overview'] },
  { name: 'Live Chats', path: '/chats', icon: MessageSquare, keywords: ['chat', 'messages', 'conversations'] },
  { name: 'Calls', path: '/calls', icon: Phone, keywords: ['phone', 'voice', 'call log'] },
  { name: 'Bookings Calendar', path: '/bookings', icon: Calendar, keywords: ['booking', 'consultation', 'appointment', 'scheduled', 'calendar'] },
  { name: 'Team Messages', path: '/messages', icon: Mail, keywords: ['message', 'chat', 'team', 'internal', 'dm'] },
  { name: 'AI Agents', path: '/agents', icon: Bot, keywords: ['agent', 'assistant', 'inbound', 'outbound', 'ai'] },
  { name: 'Chat History', path: '/conversations', icon: Archive, keywords: ['conversations', 'past', 'logs', 'history'] },
  { name: 'Analytics', path: '/analytics', icon: BarChart3, keywords: ['stats', 'metrics', 'reports', 'data'] },
  { name: 'Team & Users', path: '/team', icon: Users2, keywords: ['members', 'staff', 'roles', 'invite'] },
  { name: 'Widget & Embed', path: '/widget', icon: Code, keywords: ['embed', 'chat widget', 'website'] },
  { name: 'Integrations', path: '/integrations', icon: Plug, keywords: ['connect', 'api', 'third party'] },
  { name: 'Form Connections', path: '/form-connections', icon: Webhook, keywords: ['forms', 'webhooks', 'leads'] },
  { name: 'Settings', path: '/settings', icon: Settings, keywords: ['profile', 'account', 'preferences', 'billing'] },
]

const adminPages = [
  { name: 'The Castle', path: '/admin', icon: Shield, keywords: ['admin', 'platform', 'castle'] },
  { name: 'Organizations', path: '/admin/organizations', icon: Users, keywords: ['orgs', 'tenants'] },
  { name: 'Voice & Costs', path: '/admin/voice', icon: Phone, keywords: ['voice management', 'costs', 'telnyx'] },
]

export default function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate()
  const isPlatformAdmin = (() => {
    try {
      const token = localStorage.getItem('app_auth_token')
      if (!token) return false
      const payload = JSON.parse(atob(token.split('.')[1]))
      return payload.isPlatformAdmin === true
    } catch { return false }
  })()

  const handleSelect = (path: string) => {
    onOpenChange(false)
    navigate(path)
  }

  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        onOpenChange(!open)
      }
    }

    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [open, onOpenChange])

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search pages, agents, settings..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandGroup heading="Pages">
          {pages.map((page) => {
            const Icon = page.icon
            return (
              <CommandItem
                key={page.path}
                value={`${page.name} ${page.keywords.join(' ')}`}
                onSelect={() => handleSelect(page.path)}
                className="cursor-pointer"
              >
                <Icon className="mr-2 h-4 w-4" />
                <span>{page.name}</span>
              </CommandItem>
            )
          })}
        </CommandGroup>
        {isPlatformAdmin && <CommandGroup heading="Admin">
          {adminPages.map((page) => {
            const Icon = page.icon
            return (
              <CommandItem
                key={page.path}
                value={`${page.name} ${page.keywords.join(' ')}`}
                onSelect={() => handleSelect(page.path)}
                className="cursor-pointer"
              >
                <Icon className="mr-2 h-4 w-4" />
                <span>{page.name}</span>
              </CommandItem>
            )
          })}
        </CommandGroup>}
        <CommandGroup heading="Quick Actions">
          <CommandItem
            value="search conversations messages"
            onSelect={() => handleSelect('/conversations')}
            className="cursor-pointer"
          >
            <Search className="mr-2 h-4 w-4" />
            <span>Search conversations...</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  )
}
